// src/features/categories/components/CategoryQuickCreate.tsx
// ENHANCED QUICK CREATE CATEGORY MODAL

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { createCategory } from '@/features/categories/services/categoriesApi';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import {
    X, Save, FolderTree, ChevronRight, Info,
    AlertCircle, CheckCircle, Zap, Tag, Hash,
    AlignLeft, Layers, Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface CategoryQuickCreateProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (category: CreatedCategory) => void;
    suggestedSlug?: string;
    suggestedName?: string;
    existingCategories: Array<{
        id: string;
        name: string;
        slug: string;
        parentId: string | null;
    }>;
    parentId?: string;
}

export interface CreatedCategory {
    id: string;
    name: string;
    slug: string;
    parentId: string | null;
}

// ============================================================
// HELPERS
// ============================================================

function slugToName(slug: string): string {
    return slug
        .split('-')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function nameToSlug(name: string): string {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

function validateSlug(slug: string): string | undefined {
    if (!slug) return 'Slug is required';
    if (slug.length > 255) return 'Slug must not exceed 255 characters';
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
        return 'Only lowercase letters, numbers, and hyphens allowed';
    }
    if (slug.startsWith('-') || slug.endsWith('-')) {
        return 'Cannot start or end with a hyphen';
    }
    return undefined;
}

function extractErrorMessage(err: any): string {
    const apiError = err?.response?.data?.error;
    if (!apiError) return err?.message || 'An unexpected error occurred.';
    if (apiError.details && Array.isArray(apiError.details) && apiError.details.length > 0) {
        return apiError.details.map((d: any) => d.message).join('. ');
    }
    return apiError.message || 'An unexpected error occurred.';
}

// ============================================================
// COMPONENT
// ============================================================

export function CategoryQuickCreate({
    isOpen,
    onClose,
    onCreated,
    suggestedSlug,
    suggestedName,
    existingCategories,
    parentId,
}: CategoryQuickCreateProps) {
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [name, setName] = useState<string>(() => {
        if (suggestedName) return suggestedName;
        if (suggestedSlug) return slugToName(suggestedSlug);
        return '';
    });

    const [slug, setSlug] = useState<string>(() => {
        if (suggestedSlug) return suggestedSlug;
        if (suggestedName) return nameToSlug(suggestedName);
        return '';
    });

    const [description, setDescription] = useState<string>('');
    const [selectedParentId, setSelectedParentId] = useState<string>(parentId || '');
    const [slugManuallyEdited, setSlugManuallyEdited] = useState<boolean>(false);
    const [slugError, setSlugError] = useState<string | undefined>(undefined);
    const [nameError, setNameError] = useState<string | undefined>(undefined);
    const [isFocused, setIsFocused] = useState<'name' | 'slug' | null>(null);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            const initialName = suggestedName || (suggestedSlug ? slugToName(suggestedSlug) : '');
            const initialSlug = suggestedSlug || (suggestedName ? nameToSlug(suggestedName) : '');
            setName(initialName);
            setSlug(initialSlug);
            setDescription('');
            setSelectedParentId(parentId || '');
            setSlugManuallyEdited(false);
            setSlugError(undefined);
            setNameError(undefined);
        }
    }, [isOpen, suggestedSlug, suggestedName, parentId]);

    // Check for duplicates
    const slugExists: boolean = existingCategories.some(
        (c) => c.slug.toLowerCase() === slug.toLowerCase()
    );
    const nameExists: boolean = existingCategories.some(
        (c) => c.name.toLowerCase() === name.toLowerCase()
    );

    // Validate slug in real-time
    useEffect(() => {
        if (slug) {
            setSlugError(validateSlug(slug));
        } else {
            setSlugError('Slug is required');
        }
    }, [slug]);

    const createMutation = useMutation({
        mutationFn: () =>
            createCategory(user!.tenantId, {
                name: name.trim(),
                slug: slug.trim(),
                description: description.trim() || undefined,
                parentId: selectedParentId || undefined,
                sortOrder: existingCategories.length,
            }),
        onSuccess: (data: CreatedCategory) => {
            queryClient.invalidateQueries({ queryKey: ['categories-tree'] });
            queryClient.invalidateQueries({ queryKey: ['categories-flat'] });
            addToast({
                type: 'success',
                title: 'Category Created! 🎉',
                message: `"${data.name}" has been added successfully.`,
            });
            onCreated(data);
            onClose();
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Creation Failed',
                message: extractErrorMessage(err),
            });
        },
    });

    const handleSubmit = (e: React.FormEvent): void => {
        e.preventDefault();

        if (!name.trim()) {
            setNameError('Category name is required');
            return;
        }
        setNameError(undefined);

        if (!slug.trim() || slugError) {
            return;
        }

        if (slugExists) {
            setSlugError(`A category with slug "${slug}" already exists. Please choose a different slug.`);
            return;
        }

        createMutation.mutate();
    };

    const handleNameChange = (value: string): void => {
        setName(value);
        setNameError(undefined);

        if (!slugManuallyEdited) {
            const newSlug = nameToSlug(value);
            setSlug(newSlug);
        }
    };

    const handleSlugChange = (value: string): void => {
        setSlug(value.toLowerCase());
        setSlugManuallyEdited(true);
    };

    const isFormValid = name.trim() && slug.trim() && !slugError && !slugExists;

    // Get parent categories (top level only for simplicity)
    const parentCategories = existingCategories.filter((c) => !c.parentId);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 overflow-y-auto"
                >
                    <div className="flex items-center justify-center min-h-screen px-4 py-8">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                            onClick={onClose}
                        />

                        {/* Modal */}
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
                        >
                            {/* Header */}
                            <div className="px-8 py-5 bg-gradient-to-r from-emerald-600 to-teal-600">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-white/20 backdrop-blur-sm">
                                            <Zap className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-white">
                                                Quick Create Category
                                            </h2>
                                            <p className="text-emerald-100 text-xs">
                                                Add a new category on the fly
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors"
                                        type="button"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Suggested Info Banner */}
                            {suggestedName && (
                                <div className="mx-8 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2">
                                    <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs text-blue-700 font-medium">
                                            Suggested from product form
                                        </p>
                                        <p className="text-[11px] text-blue-500 mt-0.5">
                                            You can edit the name and slug before creating.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Body */}
                            <div className="p-8">
                                <form id="quick-category-form" onSubmit={handleSubmit} className="space-y-5">
                                    {/* Category Name */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                            Category Name <span className="text-rose-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    handleNameChange(e.target.value)
                                                }
                                                onFocus={() => setIsFocused('name')}
                                                onBlur={() => setIsFocused(null)}
                                                placeholder="e.g., Beverages & Drinks"
                                                className={clsx(
                                                    'w-full pl-10 pr-4 py-3.5 border-2 rounded-xl text-sm font-medium transition-all',
                                                    'focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white',
                                                    'placeholder:text-slate-400',
                                                    nameError
                                                        ? 'border-rose-300 bg-rose-50'
                                                        : isFocused === 'name'
                                                            ? 'border-emerald-400 bg-white'
                                                            : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                                                )}
                                                autoFocus
                                            />
                                        </div>
                                        {nameError && (
                                            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1.5">
                                                <AlertCircle size={12} />
                                                {nameError}
                                            </p>
                                        )}
                                        {nameExists && !nameError && !createMutation.isPending && (
                                            <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1.5">
                                                <AlertCircle size={12} />
                                                A category with this name already exists
                                            </p>
                                        )}
                                        {name.trim() && !nameError && !nameExists && (
                                            <p className="text-xs text-emerald-500 mt-1.5 flex items-center gap-1.5">
                                                <CheckCircle size={12} />
                                                Name looks good
                                            </p>
                                        )}
                                    </div>

                                    {/* Slug */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                            Slug <span className="text-rose-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input
                                                type="text"
                                                value={slug}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    handleSlugChange(e.target.value)
                                                }
                                                onFocus={() => setIsFocused('slug')}
                                                onBlur={() => setIsFocused(null)}
                                                placeholder="e.g., beverages-drinks"
                                                className={clsx(
                                                    'w-full pl-10 pr-16 py-3.5 border-2 rounded-xl text-sm font-mono transition-all',
                                                    'focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white',
                                                    'placeholder:text-slate-400',
                                                    slugError || slugExists
                                                        ? 'border-rose-300 bg-rose-50'
                                                        : !slugManuallyEdited && name
                                                            ? 'border-amber-200 bg-amber-50'
                                                            : isFocused === 'slug'
                                                                ? 'border-emerald-400 bg-white'
                                                                : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                                                )}
                                            />
                                            {/* Auto-generated indicator */}
                                            {!slugManuallyEdited && name && !slugError && (
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-amber-600 font-semibold bg-amber-100 px-2 py-0.5 rounded-full">
                                                    Auto
                                                </span>
                                            )}
                                            {/* Valid indicator */}
                                            {slugManuallyEdited && !slugError && !slugExists && slug && (
                                                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                                                    <CheckCircle size={14} className="text-emerald-500" />
                                                </span>
                                            )}
                                        </div>
                                        {slugError && (
                                            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1.5">
                                                <AlertCircle size={12} />
                                                {slugError}
                                            </p>
                                        )}
                                        {slugExists && !createMutation.isPending && (
                                            <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1.5">
                                                <AlertCircle size={12} />
                                                Slug already taken - please modify
                                            </p>
                                        )}
                                        {!slugError && !slugExists && slug && (
                                            <p className="text-xs text-emerald-500 mt-1.5 flex items-center gap-1.5">
                                                <CheckCircle size={12} />
                                                Slug is valid and available
                                            </p>
                                        )}
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                            Description <span className="text-slate-400 font-normal">(optional)</span>
                                        </label>
                                        <div className="relative">
                                            <AlignLeft className="absolute left-3 top-4 w-4 h-4 text-slate-400" />
                                            <textarea
                                                value={description}
                                                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                                    setDescription(e.target.value)
                                                }
                                                rows={2}
                                                className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 bg-slate-50 rounded-xl text-sm resize-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white transition-all placeholder:text-slate-400"
                                                placeholder="Brief description of this category..."
                                            />
                                        </div>
                                    </div>

                                    {/* Parent Category */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                            Parent Category <span className="text-slate-400 font-normal">(optional)</span>
                                        </label>
                                        <div className="relative">
                                            <Layers className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <select
                                                value={selectedParentId}
                                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                                    setSelectedParentId(e.target.value)
                                                }
                                                className="w-full pl-10 pr-4 py-3.5 border-2 border-slate-200 bg-slate-50 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white transition-all appearance-none cursor-pointer"
                                            >
                                                <option value="">None (Top Level Category)</option>
                                                {parentCategories.map((c) => (
                                                    <option key={c.id} value={c.id}>
                                                        {c.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 rotate-90 pointer-events-none" />
                                        </div>
                                        {selectedParentId && (
                                            <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1">
                                                <FolderTree size={12} />
                                                Will be created as a sub-category
                                            </p>
                                        )}
                                    </div>

                                    {/* Quick Preview */}
                                    {(name || slug) && (
                                        <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-emerald-50/50 border border-slate-200">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">
                                                Preview
                                            </p>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold shadow-md">
                                                    {name ? name.charAt(0).toUpperCase() : '?'}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-bold text-slate-800 truncate">
                                                        {name || 'Category Name'}
                                                    </p>
                                                    <code className="text-[11px] text-slate-400">
                                                        /{slug || 'category-slug'}
                                                    </code>
                                                </div>
                                                {selectedParentId && (
                                                    <Badge variant="info" size="sm" className="ml-auto flex-shrink-0">
                                                        Sub-category
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </form>
                            </div>

                            {/* Footer */}
                            <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between rounded-b-3xl">
                                <button
                                    onClick={onClose}
                                    className="px-5 py-3 border-2 border-slate-300 rounded-xl text-sm font-medium text-slate-600 hover:bg-white transition-colors"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <div className="flex items-center gap-3">
                                    {createMutation.isPending && (
                                        <span className="text-xs text-slate-400 flex items-center gap-1.5">
                                            <Spinner size="xs" />
                                            Creating...
                                        </span>
                                    )}
                                    <button
                                        form="quick-category-form"
                                        type="submit"
                                        disabled={!isFormValid || createMutation.isPending}
                                        className={clsx(
                                            'px-6 py-3 rounded-xl text-sm font-semibold transition-all flex items-center gap-2',
                                            isFormValid
                                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/25 hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl'
                                                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                        )}
                                    >
                                        {createMutation.isPending ? (
                                            <>
                                                <Spinner size="xs" variant="white" />
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles size={14} />
                                                Create Category
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}